import {
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  alpha,
} from "@mui/material";
import type {
  BundleEntry,
  Location,
  Practitioner,
  PractitionerRole,
} from "fhir/r5";

interface ScheduleAdministrativeFiltersProps {
  selectedLocation: string;
  selectedPractitioner: string;
  onLocationChange: (locationId: string) => void;
  onPractitionerChange: (practitionerId: string) => void;
  disabled?: boolean;
  // Dados dos contextos
  location?: BundleEntry<Location>[];
  locationLoading?: boolean;
  practitionerRole?: BundleEntry<PractitionerRole>[];
  practitioner?: BundleEntry<Practitioner>[];
  practitionerRoleLoading?: boolean;
}

/**
 * Componente de filtros administrativos específico para o ProfessionalSchedule
 * Permite filtrar por localização e profissional com estilo adaptado ao Schedule
 *
 * @example
 * ```tsx
 * const { selectedLocation, handleLocationChange } = useAdministrativeFilters();
 *
 * return (
 *   <ScheduleAdministrativeFilters
 *     selectedLocation={selectedLocation}
 *     onLocationChange={handleLocationChange}
 *     ...
 *   />
 * );
 * ```
 */
export function ScheduleAdministrativeFilters({
  selectedLocation,
  selectedPractitioner,
  onLocationChange,
  onPractitionerChange,
  disabled,
  location,
  locationLoading,
  practitionerRole,
  practitioner,
  practitionerRoleLoading,
}: ScheduleAdministrativeFiltersProps) {
  return (
    <Stack direction="row" spacing={2}>
      {/* Filtro de Localização */}
      <FormControl
        size="small"
        disabled={disabled || locationLoading}
        sx={{
          minWidth: 200,
          "& .MuiOutlinedInput-root": {
            color: "white",
            bgcolor: alpha("#fff", 0.1),
            "&:hover": { bgcolor: alpha("#fff", 0.2) },
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
          },
        }}
      >
        <Select
          value={selectedLocation}
          onChange={(e) => onLocationChange(e.target.value)}
          label="Localização"
        >
          <MenuItem value="all">Todas as localizações</MenuItem>
          {location
            ?.filter((loc) => loc.resource?.status !== "inactive")
            ?.map((loc) => (
              <MenuItem key={loc.resource?.id} value={loc.resource?.id}>
                {loc.resource?.name}
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      {/* Filtro de Profissional */}
      <FormControl
        size="small"
        disabled={disabled || practitionerRoleLoading}
        sx={{
          minWidth: 200,
          "& .MuiOutlinedInput-root": {
            color: "white",
            bgcolor: alpha("#fff", 0.1),
            "&:hover": { bgcolor: alpha("#fff", 0.2) },
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
          },
        }}
      >
        <Select
          value={selectedPractitioner}
          onChange={(e) => onPractitionerChange(e.target.value)}
          label="Profissional"
        >
          <MenuItem value="all">Todos os profissionais</MenuItem>
          {practitionerRole
            ?.filter((role) => role.resource?.active !== false)
            ?.map((role: any) => {
              // Busca os detalhes do profissional associado ao role
              const practId =
                role.resource?.practitioner?.reference?.split("/")[1];
              const pract = practitioner?.find(
                (p: any) => p.resource?.id === practId
              );

              // Informações para exibição
              const displayName =
                pract?.resource?.name?.[0]?.text ||
                `${pract?.resource?.name?.[0]?.given?.join(" ")} ${
                  pract?.resource?.name?.[0]?.family
                }`;
              const specialty =
                role.resource?.specialty?.[0]?.coding?.[0]?.display || "";

              return (
                <MenuItem key={role.resource?.id} value={role.resource?.id}>
                  {displayName}
                  {specialty ? ` - ${specialty}` : ""}
                </MenuItem>
              );
            })}
        </Select>
      </FormControl>
    </Stack>
  );
}
